import { Box, Stack, ToggleButtonGroup, Tooltip, Switch, FormControlLabel } from '@mui/material'
import GrainIcon from '@mui/icons-material/Grain'
import TuneIcon from '@mui/icons-material/Tune'
import { relayout } from 'plotly.js'
import BoxSelectIcon from 'components/shared/icons/BoxSelectIcon'
import DeepcellPrimaryIconButton from 'components/shared/DeepcellPrimaryIconButton'
import DeepcellToggleButton from 'components/shared/DeepcellToggleButton'
import CameraIcon from 'components/shared/icons/CameraIcon'
import LassoIcon from 'components/shared/icons/LassoIcon'
import PanIcon from 'components/shared/icons/PanIcon'
import ScaleIcon from 'components/shared/icons/ScaleIcon'
import { useCallback, useMemo, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { ModebarAction, ModebarDragAction, modebarDragActions } from 'redux/slices'
import { usePlotlySlice } from 'redux/slices/hooks'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import useEventsManager from 'redux/slices/hooks/useEventsManager'
import { usePlotData } from '../plot/usePlotData'
import usePlotDataWithScale from '../plot/usePlotDataWithScale'
import { calculatePlotRangeWithPadding } from '../plot/utils'
import { TuneHyperparametersDialog } from '../startAnalysis/TuneHyperparametersDialog'

interface UsePlotlyModebarOptions {
  onOpenTuneDialog?: () => void
}

export const usePlotlyModebar = (options?: UsePlotlyModebarOptions): {
  dragMode: ModebarDragAction
  triggerModebarButton: (action: ModebarAction, resetWithPadding?: boolean) => void
} => {
  const {
    setDragMode,
    plotly: { dragMode },
  } = usePlotlySlice()
  const { plotData } = usePlotData()
  const plotDataWithScale = usePlotDataWithScale(plotData, 'scatter', false)
  const { onOpenTuneDialog } = options ?? {}

  const triggerModebarButton = useCallback(
    (action: ModebarAction, resetWithPadding?: boolean) => {
      if (!action) return

      if (action === 'Tune Hyperparameters') {
        onOpenTuneDialog?.()
        return
      }

      // For Leiden Select mode, use Pan as the underlying Plotly dragmode
      const plotlyAction = action === 'Leiden Select' ? 'Pan' : action

      const anchorElement = document.querySelector(
        `[data-title="${plotlyAction}"]`
      ) as HTMLAnchorElement
      if (resetWithPadding) {
        const plotElement = document.getElementById('scatter-plot')
        if (plotElement) {
          const { rangeX, rangeY } = calculatePlotRangeWithPadding(plotDataWithScale)
          relayout('scatter-plot', {
            xaxis: { range: rangeX },
            yaxis: { range: rangeY },
          })
        }
      } else {
        anchorElement?.click()
      }

      const dragAction = action as ModebarDragAction
      if (modebarDragActions.includes(dragAction)) {
        setDragMode(dragAction)
      }
    },
    [onOpenTuneDialog, plotDataWithScale, setDragMode]
  )

  return useMemo(() => ({ dragMode, triggerModebarButton }), [dragMode, triggerModebarButton])
}

export function ToolButtons(): JSX.Element {
  const {
    dataFrame,
    cellVisualizations: { groupCompare },
    visibleSelectedComparisonDimensions,
    setGroupCompareDragDropEnabled,
  } = useCellVisualizationsSlice()

  const [tuneDialogOpen, setTuneDialogOpen] = useState(false)

  const handleOpenTuneDialog = useCallback(() => setTuneDialogOpen(true), [])
  const handleCloseTuneDialog = useCallback(() => setTuneDialogOpen(false), [])

  const { resetRange } = usePlotlySlice()
  const eventsManager = useEventsManager()

  // Ensure dragDropEnabled exists, fallback to false if undefined
  const dragDropEnabled = groupCompare?.dragDropEnabled ?? false
  const { dragMode, triggerModebarButton } = usePlotlyModebar({ onOpenTuneDialog: handleOpenTuneDialog })

  const handleDragDropToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const isEnabled = event.target.checked
      setGroupCompareDragDropEnabled(isEnabled)

      // Track analytics event
      eventsManager.sendToggleReorderPlotsEvent(isEnabled)
    },
    [setGroupCompareDragDropEnabled, eventsManager]
  )

  useHotkeys('shift+B, b', () => triggerModebarButton('Box Select'))
  useHotkeys('shift+L, l', () => triggerModebarButton('Lasso Select'))
  useHotkeys('shift+C, c', () => triggerModebarButton('Leiden Select'))
  useHotkeys('shift+P, p', () => triggerModebarButton('Pan'))
  useHotkeys('shift+R, r', () => triggerModebarButton('Autoscale'))
  useHotkeys('shift+T, t', () => triggerModebarButton('Tune Hyperparameters'))

  return (
    <Box>
      <Stack id="dataVizToolButtons" direction="row" spacing={1}>
        <ToggleButtonGroup
          exclusive
          value={dragMode}
          disabled={!dataFrame}
          onChange={(_event, value: ModebarDragAction) => triggerModebarButton(value)}
          sx={{ border: '1px solid rgba(95, 85, 209, 0.5)' }}
        >
          <DeepcellToggleButton
            className="dataVizCellSelection"
            size="small"
            value="Box Select"
            data-testid="box-select-icon"
          >
            <Tooltip title="Box Selection (B)">
              <BoxSelectIcon />
            </Tooltip>
          </DeepcellToggleButton>
          <DeepcellToggleButton
            className="dataVizCellSelection"
            size="small"
            value="Lasso Select"
            data-testid="lasso-select-button"
          >
            <Tooltip title="Lasso Selection (L)">
              <LassoIcon />
            </Tooltip>
          </DeepcellToggleButton>
          <DeepcellToggleButton className="dataVizCellSelection" size="small" value="Leiden Select">
            <Tooltip title="Leiden Cluster Selection (C)">
              <GrainIcon />
            </Tooltip>
          </DeepcellToggleButton>
          <DeepcellToggleButton size="small" value="Pan">
            <Tooltip title="Pan (P)">
              <PanIcon />
            </Tooltip>
          </DeepcellToggleButton>
        </ToggleButtonGroup>
        <DeepcellPrimaryIconButton
          id="dataVizResetView"
          onClick={() => triggerModebarButton('Autoscale')}
          size="small"
          outlined
          icon={
            <Tooltip title="Reset View (R)">
              <ScaleIcon />
            </Tooltip>
          }
          disabled={!dataFrame}
        />
        <DeepcellPrimaryIconButton
          id="dataVizSnapshot"
          onClick={() => triggerModebarButton('Download plot as a png')}
          size="small"
          outlined
          icon={
            <Tooltip title="Save Image">
              <CameraIcon />
            </Tooltip>
          }
          disabled={!dataFrame}
        />
        <DeepcellPrimaryIconButton
          id="dataVizTuneHyperparameters"
          onClick={handleOpenTuneDialog}
          size="small"
          outlined
          icon={
            <Tooltip title="Tune Hyperparameters (T)">
              <TuneIcon />
            </Tooltip>
          }
          disabled={!dataFrame}
        />
        <TuneHyperparametersDialog
          isOpen={tuneDialogOpen}
          onClose={handleCloseTuneDialog}
        />
        {visibleSelectedComparisonDimensions && visibleSelectedComparisonDimensions.length > 0 && (
          <FormControlLabel
            control={
              <Switch
                checked={dragDropEnabled}
                onChange={(e) => {
                  const isEnabling = e.target.checked
                  handleDragDropToggle(e)
                  if (isEnabling) {
                    resetRange(true) // Reset multi-plot range in Redux
                    // Use requestAnimationFrame to ensure relayout happens after
                    // React processes the Redux state update
                    requestAnimationFrame(() => {
                      const plotElement = document.getElementById('scatter-plot')
                      if (plotElement) {
                        relayout('scatter-plot', {
                          'xaxis.autorange': true,
                          'yaxis.autorange': true,
                        })
                      }
                    })
                  }
                }}
                color="primary"
                size="small"
              />
            }
            label="Customize Column Order"
            sx={{
              marginLeft: 1,
              '& .MuiFormControlLabel-label': {
                fontSize: '12px',
                color: (t) => t.palette.primary.main,
                fontWeight: 500,
              },
            }}
          />
        )}
      </Stack>
    </Box>
  )
}

export default ToolButtons
